import { cleaningRoomRepository, cleaningTaskRepository } from '../db/database';
import type { CleaningRoom, CleaningTask, IconName, IsoDate } from '../models/types';
import {
  getTaskSchedule,
  isTaskClean,
  isTaskCompletedOn,
  syncAutomaticCleaningLog,
  type TaskSchedule,
} from '../services/cleaning';
import { queueNotificationSync } from '../services/notificationSync';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderCheckIcon, renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';

/**
 * Temizlik sayfası — "ev haritası".
 *
 * Odalar iki sütunlu bir ızgarada kart olarak durur; her kartın içinde
 * o odanın görevleri ve her görevin ne zaman yapılması gerektiği
 * yazar. Zamanlama hesabı burada YAPILMAZ, `services/cleaning.ts`'ten
 * gelir — aynı formülü gün kapanışı da kullanıyor (§7.5).
 */

interface RoomView {
  room: CleaningRoom;
  tasks: CleaningTask[];
}

interface CleaningPageData {
  today: IsoDate;
  rooms: RoomView[];
}

/* ==================================================================
   Veri
   ================================================================== */

async function loadCleaningData(): Promise<CleaningPageData> {
  const today = todayIso();

  const [rooms, tasks] = await Promise.all([
    cleaningRoomRepository.list(),
    cleaningTaskRepository.list(),
  ]);

  return {
    today,
    rooms: rooms.map((room) => ({
      room,
      tasks: tasks.filter((task) => task.roomId === room.id),
    })),
  };
}

/* ==================================================================
   HTML
   ================================================================== */

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/** Görevin kalan süre etiketi — aciliyetine göre renklenir. */
function renderScheduleLabel(schedule: TaskSchedule): string {
  return `<span class="task__due task__due--${schedule.urgency}">${schedule.label}</span>`;
}

function renderTask(task: CleaningTask, today: IsoDate): string {
  const clean = isTaskClean(task, today);
  const schedule = getTaskSchedule(task, today);

  // Dokunuşun ne yapacağı görevin durumuna göre değişir (§7.5); ekran
  // okuyucu kullanan biri de bunu bilmeli.
  const action = !clean
    ? 'temizlendi olarak işaretle'
    : isTaskCompletedOn(task, today)
      ? 'işaretlemeyi geri al'
      : 'döngüyü baştan başlat';

  return `
    <li class="task">
      <button type="button"
              class="task__check${clean ? ' task__check--clean' : ''}"
              data-task-toggle="${task.id}"
              aria-label="${escapeHtml(task.name)} — ${action}">
        ${clean ? renderCheckIcon(9, 'white', 4.5) : ''}
      </button>
      <span class="task__name">${escapeHtml(task.name)}</span>
      ${renderScheduleLabel(schedule)}
    </li>
  `;
}

function renderRoom(view: RoomView, today: IsoDate): string {
  const tasks = view.tasks.length
    ? `<ul class="task-list" role="list">${view.tasks
        .map((task) => renderTask(task, today))
        .join('')}</ul>`
    : '<p class="room-card__empty">Görev yok</p>';

  return `
    <section class="room-card">
      <header class="room-card__header">
        <span class="room-card__icon">${renderIcon(view.room.icon, { size: 16 })}</span>
        <h2 class="room-card__name">${escapeHtml(view.room.name)}</h2>
      </header>
      ${tasks}
    </section>
  `;
}

/* ==================================================================
   Etkileşim
   ================================================================== */

/**
 * Göreve dokunulduğunda ne olacağını uygular (§7.5).
 *
 * Dokunuşun anlamı görevin durumuna göre değişir:
 * - Bekliyor/gecikmiş → temizlendi olarak işaretlenir, döngü başlar.
 * - Temiz (döngü sürüyor) → döngü baştan başlar ("bugün tekrar
 *   yaptım"). `lastCompletedDate` yine bugüne çekildiği için işlem
 *   yukarıdakiyle aynıdır.
 * - Bugün işaretlenmiş → döngüyü bugüne çekmek hiçbir şeyi
 *   değiştirmeyeceğinden, bu dokunuş işaretlemeyi GERİ ALIR. Yanlış
 *   bir dokunuşu düzeltmenin tek yolu bu; saklanan önceki tarih
 *   (`previousCompletedDate`) geri konur.
 *
 * Sonrasında otomatik "Temizlik" alışkanlığının bugünkü kaydı
 * tazeleniyor: kullanıcı son bekleyen görevi de bitirdiyse, Anasayfa'ya
 * döndüğünde rozeti yeşil görmeli.
 */
async function toggleTask(task: CleaningTask, today: IsoDate): Promise<void> {
  const isDoneToday = isTaskCompletedOn(task, today);

  const updated: CleaningTask = isDoneToday
    ? {
        ...task,
        lastCompletedDate: task.previousCompletedDate,
        previousCompletedDate: null,
      }
    : {
        ...task,
        lastCompletedDate: today,
        previousCompletedDate: task.lastCompletedDate,
      };

  await cleaningTaskRepository.upsert(updated);
  await syncAutomaticCleaningLog(today);
  // §7.8.3 — Görevin bir sonraki yapılma tarihi değişti; sunucu bunu
  // bilmezse yapılmış bir iş için hatırlatma göndermeye devam ederdi.
  queueNotificationSync();
}

function attachHandlers(
  container: HTMLElement,
  data: CleaningPageData,
  navigate: (route: RouteId) => Promise<void>,
): void {
  const tasksById = new Map(
    data.rooms.flatMap((view) => view.tasks.map((task) => [task.id, task] as const)),
  );

  // Tek dinleyici: görev satırları işaretleme sonrası yeniden
  // çiziliyor, her düğmeye ayrı dinleyici bağlamak onları her
  // seferinde yeniden bağlamayı gerektirirdi.
  // `setDelegatedClick`, bir önceki çizimin dinleyicisini kaldırır —
  // aksi halde ikinci dokunuş iki kez işlenir ve görev işaretlenip
  // hemen geri alınırdı.
  setDelegatedClick('cleaning', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest<HTMLElement>('[data-task-toggle]');
    if (!button) return;

    const task = tasksById.get(button.dataset['taskToggle'] ?? '');
    if (!task) return;

    void (async () => {
      await toggleTask(task, data.today);
      // Bir görevin durumu diğerlerinin etiketlerini etkilemez, ama
      // sayfayı baştan çizmek hem basit hem de yeterince hızlı.
      await renderCleaningPage(container, navigate);
    })();
  });
}

/* ==================================================================
   Başlangıç verisi
   ================================================================== */

interface RoomSeed {
  name: string;
  icon: IconName;
  tasks: { name: string; cycleDays: number }[];
}

/**
 * İlk açılışta eklenen örnek odalar ve görevler.
 *
 * GEÇİCİ: Faz 10'da oda/görev yönetim ekranı gelince kullanıcı
 * bunları tamamen değiştirebilecek. §7.5 gereği sabit bir oda listesi
 * YOKTUR — bunlar yalnızca sayfanın boş açılmaması için bir başlangıç.
 */
const SEED_ROOMS: RoomSeed[] = [
  {
    name: 'Mutfak',
    icon: 'home',
    tasks: [
      { name: 'Bulaşık yıkama', cycleDays: 1 },
      { name: 'Ocak temizliği', cycleDays: 7 },
    ],
  },
  {
    name: 'Yatak Odası',
    icon: 'moon',
    tasks: [
      { name: 'Yatak toplama', cycleDays: 1 },
      { name: 'Baza tozu alma', cycleDays: 14 },
    ],
  },
  {
    name: 'Banyo',
    icon: 'water',
    tasks: [{ name: 'Duş temizliği', cycleDays: 3 }],
  },
  {
    name: 'Salon',
    icon: 'checklist',
    tasks: [{ name: 'Halı süpürme', cycleDays: 5 }],
  },
];

export async function seedCleaningIfEmpty(): Promise<void> {
  const existing = await cleaningRoomRepository.list();
  if (existing.length > 0) return;

  for (const [roomIndex, seed] of SEED_ROOMS.entries()) {
    const roomId = crypto.randomUUID();
    await cleaningRoomRepository.upsert({
      id: roomId,
      name: seed.name,
      icon: seed.icon,
      sortOrder: roomIndex,
    });

    for (const [taskIndex, task] of seed.tasks.entries()) {
      await cleaningTaskRepository.upsert({
        id: crypto.randomUUID(),
        roomId,
        name: task.name,
        cycleDays: task.cycleDays,
        // Hiç yapılmamış sayılır: kullanıcı ilk kez işaretleyince
        // döngü başlar.
        lastCompletedDate: null,
        previousCompletedDate: null,
        sortOrder: taskIndex,
      });
    }
  }
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderCleaningPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const data = await loadCleaningData();

  const rooms = data.rooms.length
    ? `<div class="room-grid">${data.rooms
        .map((view) => renderRoom(view, data.today))
        .join('')}</div>`
    : `<div class="placeholder">
         <p class="placeholder__title">Henüz oda yok</p>
         <p class="placeholder__hint">Aşağıdaki düzenle butonundan oda ve görev ekleyebilirsin.</p>
       </div>`;

  // Sayfa dikey bir esnek sütun: oda ızgarası üstte, düzenleme butonu
  // `margin-top: auto` ile en altta. Buton ayrıca yapışkan (sticky)
  // olduğu için liste uzadığında kaydırırken ekranda kalır.
  container.innerHTML = `
    <div class="cleaning-page">
      <h1 class="page-title">Temizlik</h1>
      <p class="page-subtitle">Ev haritası</p>
      ${rooms}
      <div class="fab-bar">
        <button type="button" class="fab" data-edit-tasks>
          ${renderIcon('pencil', { size: 17, color: 'white' })}
          Görevleri Düzenle
        </button>
      </div>
    </div>
  `;

  attachHandlers(container, data, navigate);

  container.querySelector('[data-edit-tasks]')?.addEventListener('click', () => {
    void navigate('cleaningEdit');
  });
}
